import { useState } from "react";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import {
  ArrowUp, ArrowDown, Trash2, Bold, Italic, Underline,
  AlignLeft, AlignCenter, AlignRight, Upload, Loader2, Eye, EyeOff,
  Code, Type, Image, Minus, Film, Link, Columns, GitCompare
} from "lucide-react";
import axios from "axios";
import { uploadVideoFile } from "../../lib/videoUpload";
import { API_URL, FONTS, FONT_SIZES } from "./constants";
import { RichTextEditor } from "../ui/RichTextEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/** Resolve image URLs — turns relative /api/... paths into absolute, and fixes old domain URLs */
export function resolveImageUrl(url) {
  if (!url) return "";
  // Already a relative path — prepend current backend
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  // Old absolute URL from a previous domain — extract the /api/... part
  const apiIdx = url.indexOf("/api/uploads/");
  if (apiIdx > 0) return `${BACKEND_URL}${url.slice(apiIdx)}`;
  // External URL or already correct
  return url;
}

// ======================== BLOCK HEADER (shared) ========================
export function BlockHeader({ icon: Icon, label, preview, setPreview, onMoveUp, onMoveDown, onRemove, isFirst, isLast, labelColor, children }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-3.5 h-3.5 ${labelColor || "text-slate-400"}`} />
      <span className={`text-[10px] uppercase font-bold tracking-wide ${labelColor || "text-slate-400"}`}>{label}</span>
      {children}
      <div className="ml-auto flex gap-0.5">
        {setPreview && (
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setPreview(!preview)}>
            {preview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </Button>
        )}
        {!isFirst && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveUp}><ArrowUp className="w-3 h-3" /></Button>}
        {!isLast && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveDown}><ArrowDown className="w-3 h-3" /></Button>}
        <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}

// ======================== HTML BLOCK EDITOR ========================
export function HtmlBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [preview, setPreview] = useState(true);
  const updateField = (key, val) => onChange({ ...block, [key]: val });
  return (
    <div className="border border-slate-200 rounded-lg p-3 group relative bg-white" data-testid={`block-html-${block.id}`}>
      <BlockHeader icon={Code} label="HTML / Embed Code" preview={preview} setPreview={setPreview} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove} isFirst={isFirst} isLast={isLast} />
      {!preview ? (
        <>
          <Textarea value={typeof block.content === 'object' && block.content?.html ? block.content.html : (block.content || "")} onChange={e => updateField("content", e.target.value)} rows={4} className="text-sm font-mono" placeholder={'<img src="https://..." alt="Photo" />\n<div>Your HTML or embed code here...</div>'} />
          <p className="text-[10px] text-slate-400 mt-1.5">Supports any HTML including &lt;img&gt;, &lt;table&gt;, &lt;div&gt;, inline styles, etc.</p>
        </>
      ) : (
        <div className="border border-slate-100 rounded-md p-3 min-h-[60px] bg-slate-50/50 text-sm overflow-auto">
          {block.content ? <div dangerouslySetInnerHTML={{ __html: typeof block.content === 'object' && block.content?.html ? block.content.html : block.content }} /> : <p className="text-slate-400 text-xs italic">No HTML content to preview</p>}
        </div>
      )}
    </div>
  );
}

// ======================== IMAGE BLOCK EDITOR ========================
export function ImageBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const updateField = (key, val) => onChange({ ...block, [key]: val });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/uploads?entity_type=templates&tag=template-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ ...block, url: res.data.url, original_name: res.data.original_name });
      toast.success("Photo uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  const width = block.width === "full" ? "100%" : `${block.width}%`;
  const imgSrc = resolveImageUrl(block.url);

  return (
    <div className="border border-slate-200 rounded-lg p-3 group relative bg-white" data-testid={`block-image-${block.id}`}>
      <BlockHeader icon={Image} label="Image" preview={preview} setPreview={setPreview} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove} isFirst={isFirst} isLast={isLast} />
      {preview ? (
        <div className="border border-slate-100 rounded-md p-3 min-h-[60px] bg-slate-50/50">
          {imgSrc ? (
            <div style={{ textAlign: block.textAlign || "center" }}>
              <img src={imgSrc} alt={block.alt || ""} className="rounded-md inline-block" style={{ maxWidth: width }} />
            </div>
          ) : <p className="text-slate-400 text-xs italic text-center">No image set</p>}
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-2">
            <Input value={block.url || ""} onChange={e => updateField("url", e.target.value)} className="h-9 text-sm flex-1" placeholder="Image URL (https://...) or upload a photo" />
            <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-xs font-medium text-slate-600 transition-colors whitespace-nowrap" data-testid={`upload-image-${block.id}`}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Uploading..." : "Upload Photo"}
              <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
            </label>
          </div>
          <div className="flex gap-3 items-center">
            <Input value={block.alt || ""} onChange={e => updateField("alt", e.target.value)} className="h-8 text-xs flex-1" placeholder="Alt text (optional)" />
            <Select value={block.width || "full"} onValueChange={v => updateField("width", v)}>
              <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full Width</SelectItem>
                <SelectItem value="75">75%</SelectItem>
                <SelectItem value="50">50%</SelectItem>
                <SelectItem value="25">25%</SelectItem>
              </SelectContent>
            </Select>
            <Select value={block.textAlign || "center"} onValueChange={v => updateField("textAlign", v)}>
              <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {imgSrc && (
            <div className="mt-2 rounded-md border border-dashed border-slate-200 p-2 bg-slate-50/50" style={{ textAlign: block.textAlign || "center" }}>
              <p className="text-[10px] text-slate-400 mb-1 uppercase tracking-wide">Preview at chosen width ({block.width === "full" ? "Full" : `${block.width}%`})</p>
              <img src={imgSrc} alt={block.alt||""} className="rounded inline-block" style={{ width, height: "auto" }} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ======================== TEXT BLOCK ========================
export function TextBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [preview, setPreview] = useState(false);
  const updateField = (key, val) => onChange({ ...block, [key]: val });
  return (
    <div className="border border-slate-200 rounded-lg p-3 group relative bg-white" data-testid={`block-text-${block.id}`}>
      <BlockHeader icon={Type} label="Text Block" preview={preview} setPreview={setPreview} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove} isFirst={isFirst} isLast={isLast} />
      {preview ? (
        <div className="border border-slate-100 rounded-md p-3 min-h-[40px] bg-slate-50/50">
          {block.content ? (
            <p style={{ fontFamily: block.fontFamily || "Inter", fontSize: `${block.fontSize || 14}px`, color: block.color || "#1e293b", textAlign: block.textAlign || "left", fontWeight: block.fontWeight || "normal", fontStyle: block.fontStyle || "normal", textDecoration: block.textDecoration || "none", lineHeight: 1.6, whiteSpace: "pre-line" }}>{block.content}</p>
          ) : <p className="text-slate-400 text-xs italic">No text content</p>}
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-2 flex-wrap">
            <Select value={block.fontFamily || "Inter"} onValueChange={v => updateField("fontFamily", v)}>
              <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f}><span style={{ fontFamily: f }}>{f}</span></SelectItem>)}</SelectContent>
            </Select>
            <Select value={block.fontSize || "14"} onValueChange={v => updateField("fontSize", v)}>
              <SelectTrigger className="h-7 w-16 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{FONT_SIZES.map(s => <SelectItem key={s} value={s}>{s}px</SelectItem>)}</SelectContent>
            </Select>
            <Input type="color" value={block.color || "#1e293b"} onChange={e => updateField("color", e.target.value)} className="h-7 w-10 p-0.5 cursor-pointer" />
            <div className="flex border rounded overflow-hidden">
              {[{v:"left",I:AlignLeft},{v:"center",I:AlignCenter},{v:"right",I:AlignRight}].map(({v,I})=>(
                <button key={v} type="button" className={`h-7 w-7 flex items-center justify-center ${block.textAlign===v?"bg-slate-200":"hover:bg-slate-100"}`} onClick={()=>updateField("textAlign",v)}><I className="w-3 h-3" /></button>
              ))}
            </div>
            <div className="flex border rounded overflow-hidden">
              {[{v:"bold",I:Bold,k:"fontWeight",on:"bold",off:"normal"},{v:"italic",I:Italic,k:"fontStyle",on:"italic",off:"normal"},{v:"underline",I:Underline,k:"textDecoration",on:"underline",off:"none"}].map(({v,I,k,on,off})=>(
                <button key={v} type="button" className={`h-7 w-7 flex items-center justify-center ${block[k]===on?"bg-slate-200":"hover:bg-slate-100"}`} onClick={()=>updateField(k,block[k]===on?off:on)}><I className="w-3 h-3" /></button>
              ))}
            </div>
          </div>
          <Textarea value={block.content || ""} onChange={e => updateField("content", e.target.value)} rows={3} className="text-sm" placeholder="Enter text content..." style={{ fontFamily: block.fontFamily, fontSize: `${block.fontSize||14}px`, color: block.color, textAlign: block.textAlign, fontWeight: block.fontWeight, fontStyle: block.fontStyle, textDecoration: block.textDecoration }} />
        </>
      )}
    </div>
  );
}

// ======================== SPACER BLOCK ========================
export function SpacerBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const updateField = (key, val) => onChange({ ...block, [key]: val });
  return (
    <div className="border border-dashed border-slate-200 rounded-lg p-2 group relative flex items-center gap-3 bg-slate-50/50" data-testid={`block-spacer-${block.id}`}>
      <Minus className="w-3.5 h-3.5 text-slate-300" />
      <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide">Spacer</span>
      <Input type="number" min="5" max="200" value={block.height || 20} onChange={e => updateField("height", parseInt(e.target.value) || 20)} className="h-7 w-20 text-xs" />
      <span className="text-xs text-slate-400">px</span>
      <div className="ml-auto flex gap-1">
        {!isFirst && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveUp}><ArrowUp className="w-3 h-3" /></Button>}
        {!isLast && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveDown}><ArrowDown className="w-3 h-3" /></Button>}
        <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}

// ======================== VIDEO BLOCK ========================
// A video can be a pasted link (YouTube, Vimeo, or a direct .mp4/.mov URL)
// or a file uploaded here. Uploaded files play in a native <video> player.
const MAX_VIDEO_MB = 200;

export function VideoBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const updateField = (key, val) => onChange({ ...block, [key]: val });
  const url = resolveImageUrl(block.url || "");
  let embedUrl = "";
  if (url.includes("youtube.com/watch")) embedUrl = url.replace("watch?v=", "embed/");
  else if (url.includes("youtu.be/")) embedUrl = `https://www.youtube.com/embed/${url.split("youtu.be/")[1]}`;
  else if (/vimeo\.com\/\d+/.test(url)) embedUrl = `https://player.vimeo.com/video/${url.split("vimeo.com/")[1].split(/[?#]/)[0]}`;
  const isDirectFile = !embedUrl && (/\.(mp4|mov|webm|m4v|ogv)(\?|$)/i.test(url) || url.includes("/api/uploads/"));

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error(`Video is over ${MAX_VIDEO_MB}MB. Trim it or paste a link instead.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const data = await uploadVideoFile(file, { entityType: "templates", tag: "template-video", onProgress: setProgress });
      onChange({ ...block, url: data.url, original_name: data.original_name });
      toast.success("Video uploaded!");
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 413 ? "Video is too large to upload. Trim it or paste a link instead." : (err.response?.data?.detail || "Upload failed"));
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 group relative bg-white" data-testid={`block-video-${block.id}`}>
      <BlockHeader icon={Film} label="Video" preview={preview} setPreview={setPreview} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove} isFirst={isFirst} isLast={isLast} />
      {preview ? (
        <div className="border border-slate-100 rounded-md overflow-hidden bg-slate-50/50 min-h-[60px]">
          {!url ? (
            <p className="text-slate-400 text-xs italic p-3 text-center">No video set</p>
          ) : isDirectFile ? (
            <video controls playsInline src={url} className="w-full bg-black" preload="metadata" />
          ) : (
            <div className="aspect-video"><iframe src={embedUrl || url} title="Video" className="w-full h-full border-0" allowFullScreen /></div>
          )}
          {block.caption && <p className="text-xs text-slate-500 text-center py-1">{block.caption}</p>}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            <Input value={block.url || ""} onChange={e => updateField("url", e.target.value)} className="h-9 text-sm flex-1" placeholder="Paste a YouTube, Vimeo or video link, or upload a file" data-testid={`block-video-url-${block.id}`} />
            <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-xs font-medium text-slate-600 transition-colors whitespace-nowrap">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? `Uploading ${progress}%` : "Upload Video"}
              <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm" className="hidden" onChange={handleVideoUpload} disabled={uploading} data-testid={`block-video-upload-${block.id}`} />
            </label>
          </div>
          <p className="text-[10px] text-slate-400">MP4, MOV, M4V or WEBM, up to {MAX_VIDEO_MB}MB. Large files can take a few minutes to upload.</p>
          <Input value={block.caption || ""} onChange={e => updateField("caption", e.target.value)} className="h-8 text-xs" placeholder="Caption (optional)" />
        </div>
      )}
    </div>
  );
}

// ======================== LINK BLOCK ========================
export function LinkBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [preview, setPreview] = useState(false);
  const updateField = (key, val) => onChange({ ...block, [key]: val });
  return (
    <div className="border border-slate-200 rounded-lg p-3 group relative bg-white" data-testid={`block-link-${block.id}`}>
      <BlockHeader icon={Link} label="Link / Button" preview={preview} setPreview={setPreview} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove} isFirst={isFirst} isLast={isLast} />
      {preview ? (
        <div className="border border-slate-100 rounded-md p-4 bg-slate-50/50 text-center">
          {(block.label || block.url) ? (
            <a href={block.url || "#"} target="_blank" rel="noopener noreferrer" className="inline-block px-6 py-2.5 bg-[#0F4C81] text-white rounded-md text-sm font-medium hover:bg-[#0D4270] transition-colors">{block.label || "Link"}</a>
          ) : <p className="text-slate-400 text-xs italic">No link configured</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Input value={block.label || ""} onChange={e => updateField("label", e.target.value)} className="h-8 text-sm" placeholder="Button text" />
          <Input value={block.url || ""} onChange={e => updateField("url", e.target.value)} className="h-8 text-sm" placeholder="URL (https://...)" />
        </div>
      )}
    </div>
  );
}

// ======================== COLUMNS BLOCK ========================
export function ColumnsBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [preview, setPreview] = useState(false);
  const colCount = block.columns || 2;
  const colData = block.column_data || Array.from({ length: colCount }, () => ({ content: "" }));
  const setColCount = (n) => {
    const existing = [...colData];
    while (existing.length < n) existing.push({ content: "" });
    onChange({ ...block, columns: n, column_data: existing.slice(0, n) });
  };
  const updateCol = (idx, content) => {
    const updated = [...colData];
    updated[idx] = { ...updated[idx], content };
    onChange({ ...block, column_data: updated });
  };
  return (
    <div className="border border-blue-200 rounded-lg p-3 group relative bg-blue-50/30" data-testid={`block-columns-${block.id}`}>
      <BlockHeader icon={Columns} label="Columns" preview={preview} setPreview={setPreview} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove} isFirst={isFirst} isLast={isLast} labelColor="text-blue-500">
        <div className="flex items-center gap-1 ml-1 border rounded bg-white">
          {[2, 3, 4].map(n => (
            <button key={n} type="button" onClick={() => setColCount(n)}
              className={`h-6 w-7 text-xs font-medium rounded ${colCount === n ? "bg-[#0F4C81] text-white" : "text-slate-500 hover:bg-slate-100"}`}
            >{n}</button>
          ))}
        </div>
        <span className="text-[10px] text-slate-400">cols</span>
      </BlockHeader>
      {preview ? (
        <div className={`border border-slate-100 rounded-md p-3 bg-slate-50/50 grid gap-4 ${colCount === 2 ? "grid-cols-2" : colCount === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
          {Array.from({ length: colCount }).map((_, ci) => (
            <div key={ci} className="text-sm text-slate-600 whitespace-pre-line">{colData[ci]?.content || <span className="text-slate-300 italic text-xs">Empty</span>}</div>
          ))}
        </div>
      ) : (
        <div className={`grid gap-3 ${colCount === 2 ? "grid-cols-2" : colCount === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
          {colData.slice(0, colCount).map((col, ci) => (
            <div key={ci} className="space-y-1">
              <Label className="text-[10px] text-slate-500">Column {ci + 1}</Label>
              <Textarea value={col.content || ""} onChange={e => updateCol(ci, e.target.value)} rows={4} className="text-sm bg-white" placeholder={`Column ${ci + 1} content...`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ======================== BEFORE & AFTER BLOCK ========================
export function BeforeAfterBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [uploadingSide, setUploadingSide] = useState(null); // 'before' | 'after' | null
  const [preview, setPreview] = useState(true);
  const updateField = (key, val) => onChange({ ...block, [key]: val });

  const handleUpload = async (side, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSide(side);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/uploads?entity_type=templates&tag=before-after-${side}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ ...block, [`${side}_url`]: res.data.url });
      toast.success(`${side === "before" ? "Before" : "After"} photo uploaded!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploadingSide(null);
    e.target.value = "";
  };

  const layout = block.layout || "side_by_side";
  const beforeSrc = resolveImageUrl(block.before_url);
  const afterSrc = resolveImageUrl(block.after_url);
  const widthPct = block.width === "full" ? "100%" : `${block.width || "full"}%`;
  const isStacked = layout === "stacked";

  const PhotoSlot = ({ side }) => {
    const src = side === "before" ? beforeSrc : afterSrc;
    const label = side === "before" ? (block.before_label || "Before") : (block.after_label || "After");
    return (
      <div className="relative rounded-md overflow-hidden bg-slate-100 border border-slate-200">
        {src ? (
          <img src={src} alt={label} className="w-full h-auto object-contain" />
        ) : (
          <div className="aspect-[4/3] flex items-center justify-center text-slate-400 text-xs italic">No {label.toLowerCase()} photo</div>
        )}
        <div className="absolute top-2 left-2 bg-black/70 text-white text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded">{label}</div>
      </div>
    );
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 group relative bg-white" data-testid={`block-before-after-${block.id}`}>
      <BlockHeader icon={GitCompare} label="Before & After" preview={preview} setPreview={setPreview} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onRemove={onRemove} isFirst={isFirst} isLast={isLast} />
      {preview ? (
        <div className="border border-slate-100 rounded-md p-3 bg-slate-50/50">
          <div style={{ width: widthPct, margin: "0 auto" }}>
            {layout === "slider" && beforeSrc && afterSrc ? (
              <>
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                  <ReactCompareSlider
                    itemOne={<ReactCompareSliderImage src={beforeSrc} alt={block.before_label || "Before"} style={{ objectFit: "contain" }} />}
                    itemTwo={<ReactCompareSliderImage src={afterSrc} alt={block.after_label || "After"} style={{ objectFit: "contain" }} />}
                    style={{ width: "100%", height: "auto", minHeight: 240 }}
                  />
                </div>
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-wide text-slate-500 mt-1 px-1">
                  <span>{block.before_label || "Before"}</span>
                  <span className="italic text-slate-400">← drag →</span>
                  <span>{block.after_label || "After"}</span>
                </div>
              </>
            ) : (
              <div className={isStacked ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
                <PhotoSlot side="before" />
                <PhotoSlot side="after" />
              </div>
            )}
            {block.caption && <p className="text-center text-xs text-slate-500 mt-2 italic">{block.caption}</p>}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {["before", "after"].map((side) => (
              <div key={side} className="space-y-1.5 border border-dashed border-slate-200 rounded-md p-2">
                <Label className="text-[10px] uppercase font-bold tracking-wide text-slate-500">{side === "before" ? "Before" : "After"}</Label>
                <Input
                  value={block[`${side}_label`] || ""}
                  onChange={e => updateField(`${side}_label`, e.target.value)}
                  className="h-7 text-xs"
                  placeholder={`Label (default: ${side === "before" ? "Before" : "After"})`}
                />
                <div className="flex gap-1.5">
                  <Input
                    value={block[`${side}_url`] || ""}
                    onChange={e => updateField(`${side}_url`, e.target.value)}
                    className="h-8 text-xs flex-1"
                    placeholder="Image URL"
                  />
                  <label className="inline-flex items-center gap-1 px-2 h-8 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-[10px] font-medium text-slate-600 whitespace-nowrap" data-testid={`upload-${side}-${block.id}`}>
                    {uploadingSide === side ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    {uploadingSide === side ? "…" : "Upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(side, e)} disabled={uploadingSide !== null} />
                  </label>
                </div>
                {(side === "before" ? beforeSrc : afterSrc) && (
                  <img src={side === "before" ? beforeSrc : afterSrc} alt="" className="mt-1 w-full h-auto object-contain rounded" style={{ maxHeight: 200 }} />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={layout} onValueChange={v => updateField("layout", v)}>
              <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="side_by_side">Side-by-Side</SelectItem>
                <SelectItem value="stacked">Stacked (Top/Bottom)</SelectItem>
                <SelectItem value="slider">Drag Slider</SelectItem>
              </SelectContent>
            </Select>
            <Select value={block.width || "full"} onValueChange={v => updateField("width", v)}>
              <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full Width</SelectItem>
                <SelectItem value="75">75%</SelectItem>
                <SelectItem value="50">50%</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={block.caption || ""}
              onChange={e => updateField("caption", e.target.value)}
              className="h-8 text-xs flex-1 min-w-[180px]"
              placeholder="Caption (optional)"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ======================== DISPATCHER ========================
export function ContentBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const props = { block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast };
  if (block.type === "text") return <TextBlockEditor {...props} />;
  if (block.type === "richtext") return <RichTextBlockEditor {...props} />;
  if (block.type === "spacer") return <SpacerBlockEditor {...props} />;
  if (block.type === "image") return <ImageBlockEditor {...props} />;
  if (block.type === "before_after") return <BeforeAfterBlockEditor {...props} />;
  if (block.type === "video") return <VideoBlockEditor {...props} />;
  if (block.type === "html") return <HtmlBlockEditor {...props} />;
  if (block.type === "link") return <LinkBlockEditor {...props} />;
  if (block.type === "columns") return <ColumnsBlockEditor {...props} />;
  return null;
}

/** Rich-text block — used for terms & conditions, multi-paragraph copy. */
function RichTextBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  // Block content can be either a string or { html: "..." } depending on age.
  const html =
    typeof block.content === "object" && block.content?.html != null
      ? block.content.html
      : (typeof block.content === "string" ? block.content : "");

  const setHtml = (newHtml) => {
    // Persist as { html } so the renderer + future editors agree.
    onChange({ ...block, content: { html: newHtml } });
  };

  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white space-y-2" data-testid="block-editor-richtext">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
          <Type className="w-3 h-3" /> Rich Text
        </Label>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={isFirst} onClick={onMoveUp}><ArrowUp className="w-3 h-3" /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={isLast} onClick={onMoveDown}><ArrowDown className="w-3 h-3" /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      <RichTextEditor
        value={html}
        onChange={setHtml}
        rows={8}
        placeholder="Enter terms & conditions, warranty info, or any rich text…"
      />
    </div>
  );
}
